import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'
import { EditPlatform } from './EditPlatform'
import { 
  get_all_platforms,
  delete_platform,
  update_platform
} from '../api/platformsApi'
import { toast } from 'react-toastify'
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

// Platform type for this module
interface PlatformItem {
  id: number
  platform_name: string
  platform_url: string
  platform_logo: string | null
  description: string
  created_at: string
  updated_at: string
  status: number
}

const PlatformsList: React.FC = () => {
  const navigate = useNavigate()
  const [platforms, setPlatforms] = useState<PlatformItem[]>([])
  const [filteredPlatforms, setFilteredPlatforms] = useState<PlatformItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformItem | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [platformToDelete, setPlatformToDelete] = useState<PlatformItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const canViewPlatforms = Boolean(permissionCheck('platform-view'))
  const canCreatePlatform = Boolean(permissionCheck('platform-create'))
  const canUpdatePlatform = Boolean(permissionCheck('platform-update'))
  const canDeletePlatform = Boolean(permissionCheck('platform-delete'))

  // Load platforms
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        if (canViewPlatforms) {
          await fetchAllPlatforms()
        }
      } catch (error) {
        console.error('Error loading platforms:', error)
        toast.error('Failed to load platforms')
      } finally {
        setIsLoading(false)
      }
    }
    if (canViewPlatforms) {
      loadData()
    }
  }, [canViewPlatforms])

  const fetchAllPlatforms = async () => {
    try {
      const res = await get_all_platforms()
      const apiPlatforms = res?.data?.all_platforms || res?.data?.platforms || res?.data || []
      const all: PlatformItem[] = (Array.isArray(apiPlatforms) ? apiPlatforms : []).map((p: any) => ({
        id: Number(p.id),
        platform_name: p.platform_name,
        platform_url: p.platform_url || '',
        platform_logo: p.platform_logo || null,
        description: p.description || '',
        created_at: p.created_at,
        updated_at: p.updated_at,
        status: Number(p.status)
      }))
      setPlatforms(all)
      setFilteredPlatforms(all)
    } catch (error) {
      console.error('Error fetching platforms:', error)
      toast.error('Failed to load platforms')
      setPlatforms([])
      setFilteredPlatforms([])
    }
  }

  // Filter platforms based on search term and status
  useEffect(() => {
    let filtered = platforms

    if (searchTerm) {
      filtered = filtered.filter(platform =>
        platform.platform_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        platform.platform_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        platform.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedStatus !== '') {
      filtered = filtered.filter(platform => String(platform.status) === selectedStatus)
    }

    setFilteredPlatforms(filtered)
  }, [platforms, searchTerm, selectedStatus])

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredPlatforms.length / pageSize))
  const pageSafe = Math.min(currentPage, totalPages)
  const startIdx = (pageSafe - 1) * pageSize
  const endIdx = startIdx + pageSize
  const paginatedPlatforms = filteredPlatforms.slice(startIdx, endIdx)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, pageSize])


  const handleEditPlatform = async (updatedPlatform: PlatformItem, logoFile?: File | null) => {
    if (!canUpdatePlatform) {
      toast.error('You do not have permission to update platforms')
      return
    }
    try {
      const formData = new FormData()
      formData.append('platform_name', updatedPlatform.platform_name)
      formData.append('platform_url', updatedPlatform.platform_url)
      formData.append('description', updatedPlatform.description)
      formData.append('status', String(updatedPlatform.status))
      
      if (logoFile) {
        formData.append('platform_logo', logoFile)
      }
      
      await update_platform(String(updatedPlatform.id), formData)
      toast.success('Platform updated')
      // Refresh all platforms and rely on filters
      await fetchAllPlatforms()
      setShowEditModal(false)
      setSelectedPlatform(null)
    } catch (error: any) {
      console.error('Error updating platform:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update platform'
      toast.error(errorMessage)
    }
  }

  const handleDeleteClick = (platform: PlatformItem) => {
    if (!canDeletePlatform) {
      toast.error('You do not have permission to delete platforms')
      return
    }
    setPlatformToDelete(platform)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!canDeletePlatform) {
      toast.error('You do not have permission to delete platforms')
      return
    }
    if (!platformToDelete) return
    try {
      await delete_platform(String(platformToDelete.id))
      toast.success('Platform deleted')
      await fetchAllPlatforms()
    } catch (error: any) {
      console.error('Error deleting platform:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete platform'
      toast.error(errorMessage)
    } finally {
      setShowDeleteModal(false)
      setPlatformToDelete(null)
    }
  }

  const handleEditClick = (platform: PlatformItem) => {
    if (!canUpdatePlatform) {
      toast.error('You do not have permission to update platforms')
      return
    }
    setSelectedPlatform(platform)
    setShowEditModal(true)
  }

  // Helper function to get logo URL
  const getLogoUrl = (logo: string | null): string | null => {
    if (!logo) return null
    
    // If already a full URL (starts with http:// or https://), return as is
    if (logo.startsWith('http://') || logo.startsWith('https://')) {
      return logo
    }
    
    // Otherwise, prepend API base URL
    let baseURL = process.env.REACT_APP_API_URL || ''
    if (!baseURL) return logo
    
    // Remove /api from the end of baseURL if present
    baseURL = baseURL.replace(/\/api\/?$/, '')
    
    // Remove trailing slash from baseURL and leading slash from logo
    const cleanBaseURL = baseURL.replace(/\/$/, '')
    const cleanLogo = logo.replace(/^\//, '')
    
    return `${cleanBaseURL}/${cleanLogo}`
  }

  if (!canViewPlatforms) {
    return (
      <KTCard>
        <KTCardBody>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view platforms.
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  return (
    <>
      <LoadingProgressBar isLoading={isLoading} />
      <KTCard>
        <KTCardBody>
          <div className='d-flex flex-column flex-lg-row'>
            <div className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7'>
              <h3 className='fw-bold text-dark'>Platforms Management</h3>
              <p className='text-muted fs-6'>Manage your platforms</p>
            </div>
            {canCreatePlatform && (
              <div className='d-flex align-items-center'>
                <button
                  className='btn btn-primary'
                  onClick={() => navigate('/platforms/add')}
                >
                  <KTIcon iconName='plus' className='fs-2' />
                  Add Platform
                </button>
              </div>
            )}
          </div>
        </KTCardBody>
      </KTCard>

      <KTCard>
        <KTCardBody>
          {/* Search and Filter Section */}
          <div className='d-flex align-items-center gap-3 flex-wrap mb-6'>
            <div className='position-relative' style={{minWidth: 280}}>
              <KTIcon
                iconName='magnifier'
                className='fs-3 position-absolute top-50 translate-middle-y ms-3'
              />
              <input
                type='text'
                className='form-control form-control-solid ps-13'
                placeholder='Search platforms...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{minWidth: 280}}
              />
            </div>
            <div style={{minWidth: 160}}>
              <select
                className='form-select form-select-solid'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value=''>All Status</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </div>
            <div className='ms-auto'>
              <button
                className='btn btn-light-primary'
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('')
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Platforms Table */}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-100px'>Logo</th>
                  <th className='min-w-150px'>Name</th>
                  <th className='min-w-200px'>URL</th>
                  <th className='min-w-200px'>Description</th>
                  <th className='min-w-80px'>Status</th>
                  <th className='min-w-100px'>Updated At</th>
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({length: 6}).map((_, idx) => (
                    <tr key={`sk-${idx}`}>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-10'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-10'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-6'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-4'></span>
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0 placeholder-glow'>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm me-1 col-2'></span>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm col-2'></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : filteredPlatforms.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='text-center py-10'>
                      <div className='text-muted'>No platforms found</div>
                    </td>
                  </tr>
                ) : (
                  paginatedPlatforms.map((platform) => {
                    const logoUrl = getLogoUrl(platform.platform_logo)
                    return (
                      <tr key={platform.id}>
                        <td>
                          {logoUrl ? (
                            <div className='symbol symbol-50px me-2'>
                              <img src={logoUrl} alt={platform.platform_name} className='w-100' />
                            </div>
                          ) : (
                            <div className='symbol symbol-50px me-2 bg-light'>
                              <KTIcon iconName='abstract-38' className='fs-2x text-muted' />
                            </div>
                          )}
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <span className='text-dark fw-bold text-hover-primary fs-6'>
                                {platform.platform_name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <a 
                            href={platform.platform_url} 
                            target='_blank' 
                            rel='noopener noreferrer'
                            className='text-primary text-hover-primary d-block fs-7'
                          >
                            {platform.platform_url}
                          </a>
                        </td>
                        <td>
                          <span 
                            className='text-muted fw-bold text-muted d-block fs-7'
                            style={{
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={platform.description || 'N/A'}
                          >
                            {platform.description || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${platform.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                            {platform.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className='text-muted fw-bold text-muted d-block fs-7'>
                            {platform.updated_at ? new Date(platform.updated_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </td>
                        <td className='text-end'>
                          <div className='d-flex justify-content-end flex-shrink-0'>
                            {canUpdatePlatform && (
                              <button
                                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                onClick={() => handleEditClick(platform)}
                              >
                                <KTIcon iconName='pencil' className='fs-3' />
                              </button>
                            )}
                            {canDeletePlatform && (
                              <button
                                className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                                onClick={() => handleDeleteClick(platform)}
                              >
                                <KTIcon iconName='trash' className='fs-3' />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted fs-7'>
                Showing {filteredPlatforms.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filteredPlatforms.length)} of {filteredPlatforms.length}
              </span>
              <div className='d-flex align-items-center'>
                <span className='me-2 text-muted fs-7'>Show</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className='ms-2 text-muted fs-7'>per page</span>
              </div>
            </div>
            <ul className='pagination'>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
              </li>
              <li className='page-item active'>
                <span className='page-link'>{pageSafe} / {totalPages}</span>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        </KTCardBody>
      </KTCard>


      {/* Edit Platform Modal */}
      {showEditModal && selectedPlatform && (
        <EditPlatform
          platform={selectedPlatform}
          onClose={() => {
            setShowEditModal(false)
            setSelectedPlatform(null)
          }}
          onSave={handleEditPlatform}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_platform'
        tabIndex={-1}
        role='dialog'
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setPlatformToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setPlatformToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Platform</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this platform?
                  {platformToDelete && (
                    <div className='mt-3'>
                      <strong className='text-dark'>"{platformToDelete.platform_name}"</strong>
                    </div>
                  )}
                  <br />This action cannot be undone.
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setPlatformToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Platform
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export { PlatformsList }

